import React from "react";
import { Box, Typography, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { selectChartConfig } from "../../selectors/chart";
import { useSelector, useDispatch } from "react-redux";
import { updateChartConfig } from "../../reducer/chart.reducer";
import { TradingSettings as TradingSettingsType } from "../chartV2/chart.typings";
import { chartSettingsCheckboxCaptionSx, chartSettingsSectionDividerSx, chartSettingsSectionTitleSx } from "./chartSettingsLayout";

const checkboxLabelSx = {
    ml: 0,
    "& .MuiFormControlLabel-label": { fontSize: "14px" },
} as const;

type Props = {
    viewKey: string;
};

const TradingSettings = ({ viewKey }: Props) => {
    const dispatch = useDispatch();
    const chartConfig = useSelector(selectChartConfig(viewKey));
    const tradingSettings = chartConfig?.tradingSettings;

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, field: keyof TradingSettingsType) => {
        dispatch(updateChartConfig({
            viewKey,
            config: {
                tradingSettings: {
                    ...tradingSettings,
                    [field]: event.target.checked
                }
            },
            createIfAbsent: false
        }));
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                GENERAL
            </Typography>
            <FormGroup>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!(tradingSettings?.showBuySellButtons ?? true)}
                            onChange={(e) => handleCheckboxChange(e, "showBuySellButtons")}
                        />
                    }
                    label="Buy/sell buttons"
                    sx={checkboxLabelSx}
                />
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!tradingSettings?.oneClickTrading}
                                onChange={(e) => handleCheckboxChange(e, "oneClickTrading")}
                            />
                        }
                        label="One-click trading"
                        sx={checkboxLabelSx}
                    />
                    <Typography variant="caption" sx={chartSettingsCheckboxCaptionSx}>
                        Instantly place, modify, or cancel orders, or close, reverse positions via chart without confirmation.
                    </Typography>
                </Box>
            </FormGroup>
            <Typography variant="subtitle1" sx={{ ...chartSettingsSectionTitleSx, ...chartSettingsSectionDividerSx }}>
                TRADING SETTINGS
            </Typography>
            <FormGroup>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!(tradingSettings?.showTradeFromChart ?? true)}
                            onChange={(e) => handleCheckboxChange(e, "showTradeFromChart")}
                        />
                    }
                    label="Orders / Positions"
                    sx={checkboxLabelSx}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!(tradingSettings?.showOrderMarkersOnChart ?? true)}
                            onChange={(e) => handleCheckboxChange(e, "showOrderMarkersOnChart")}
                        />
                    }
                    label="Order Execution Markers"
                    sx={checkboxLabelSx}
                />
            </FormGroup>
        </Box>
    );
};

export default TradingSettings;